// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type ConduitSize,
  type ConduitType,
  formatDate,
  type ISOTimestamp,
  type MachineElement,
  type CustomProperty,
} from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';
import { isAfter, parseISO } from 'date-fns';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { getCustomPropertyColDefs } from 'components/Mantine/CustomProperties/getCustomPropertyColDefs';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { customSetFilterParams } from 'helpers/ag-grid/convertAgGridRequest';
import { CustomSelectComponent } from 'helpers/ag-grid/CustomSelectComponent';
import { CustomValueSetFilterComponent } from 'helpers/ag-grid/CustomSetFilterComponent';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { ViewBendQAButton } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/ViewBendQAButton';
import { BendValidationCell } from 'modules/Shop/EvolveFabrication/BendValidationCell';
import { ElementMenu } from 'modules/Shop/EvolveFabrication/ElementMenu';

type MachineDataEdit = {
  source: 'External' | 'Web';
  modifiedOn: ISOTimestamp;
};

/** The most recent change to the element's machine data, or null if its machine data has never changed. */
const getLatestMachineDataEdit = (element: MachineElement | null | undefined): MachineDataEdit | null => {
  if (isNil(element)) return null;
  const { machineDataExternalUpdatedOn, machineDataWebUpdatedOn } = element;
  if (isNil(machineDataExternalUpdatedOn)) {
    return isNil(machineDataWebUpdatedOn) ? null : { source: 'Web', modifiedOn: machineDataWebUpdatedOn };
  }
  if (isNil(machineDataWebUpdatedOn)) {
    return { source: 'External', modifiedOn: machineDataExternalUpdatedOn };
  }
  return isAfter(parseISO(machineDataExternalUpdatedOn), parseISO(machineDataWebUpdatedOn))
    ? { source: 'External', modifiedOn: machineDataExternalUpdatedOn }
    : { source: 'Web', modifiedOn: machineDataWebUpdatedOn };
};

const getMachineDataEditTooltip = (data: MachineElement) => {
  const latestEdit = getLatestMachineDataEdit(data);
  if (isNil(latestEdit)) return null;
  const changesWereOverridden = isNotNil(data.machineDataWebUpdatedOn) && isNotNil(data.machineDataExternalUpdatedOn);
  return `${latestEdit.source === 'External' ? 'Modified externally' : 'Modified in the web application'} on ${formatDate(
    latestEdit.modifiedOn,
    "PP 'at' p",
  )}. ${
    changesWereOverridden
      ? latestEdit.source === 'External'
        ? 'The previous modifications in the web have been overridden.'
        : 'The previous modifications in Revit have been overridden.'
      : ''
  }`;
};

export const getMachineElementsColumnDef = ({
  conduitTypes = [],
  conduitSizes = [],
  onRemoveElement,
  onEditElementData,
  onEditBendData,
  onChangeBenderProfile,
  isWorkOrderPage,
  isCutListView,
  allowQaModal = true,
  onElementUpdated,
  customElementProperties,
}: {
  conduitTypes: ConduitType[] | undefined;
  conduitSizes: ConduitSize[] | undefined;
  onEditElementData?: (element: MachineElement) => void;
  onEditBendData?: (element: MachineElement) => void;
  onRemoveElement?: (element: MachineElement) => void;
  onChangeBenderProfile?: (element: MachineElement) => void;
  /** If true, will hide certain columns by default */
  isWorkOrderPage?: boolean;
  isCutListView?: boolean;
  /** @defaultValue true */
  allowQaModal?: boolean;
  onElementUpdated?: (element: MachineElement) => void;
  customElementProperties: CustomProperty[];
}): ColDef<MachineElement>[] => [
  {
    field: 'materialSize',
    headerName: 'Material Size',
    width: 150,
    // Used for BE filtering
    colId: 'materialSizes',
    // NOTE: This assumes we're only showing conduit types for bends for now.
    filter: CustomValueSetFilterComponent(customSetFilterParams(conduitSizes, 'name', 'name')),
    hide: isCutListView,
    suppressColumnsToolPanel: isCutListView,
  },
  {
    field: 'materialType',
    headerName: 'Material Type',
    width: 150,
    // Used for BE filtering
    colId: 'materialTypes',
    // NOTE: This assumes we're only showing conduit types for bends for now.
    filter: CustomValueSetFilterComponent(customSetFilterParams(conduitTypes, 'name', 'name')),
    hide: isCutListView,
    suppressColumnsToolPanel: isCutListView,
  },
  {
    field: 'bendType',
    width: 130,
    minWidth: 130,
    flex: isWorkOrderPage ? 1 : undefined,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'level',
    width: 150,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    initialHide: isWorkOrderPage,
  },
  {
    field: 'location',
    width: 150,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    initialHide: isWorkOrderPage,
  },
  {
    field: 'spool',
    width: 150,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'customerId',
    headerName: 'Customer ID',
    tooltipField: 'customerId',
    width: 160,
    minWidth: 160,
    flex: isWorkOrderPage ? 1 : undefined,
    colId: 'customerIds',
    filter: 'agTextColumnFilter',
    initialHide: isCutListView,
  },
  {
    field: 'runId',
    tooltipField: 'runId',
    width: 120,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    initialHide: isWorkOrderPage && !isCutListView,
  },
  {
    field: 'sourceIds',
    headerName: 'Source IDs',
    tooltipField: 'sourceIds',
    width: 160,
    sortable: false,
    initialHide: true,
  },
  {
    field: 'workOrderIdentifier',
    headerName: 'Work Order Id',
    tooltipField: 'workOrderIdentifier',
    cellRenderer: CellRenderer<MachineElement, 'workOrderIdentifier'>(({ value, data }) =>
      data.workOrderId ? (
        <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Work Orders">
          {value}
        </EvolveLink>
      ) : (
        '--'
      ),
    ),
    hide: isWorkOrderPage,
    suppressColumnsToolPanel: isWorkOrderPage,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'workOrderName',
    headerName: 'Work Order Name',
    tooltipField: 'workOrderName',
    flex: 1,
    minWidth: 220,
    hide: isWorkOrderPage,
    suppressColumnsToolPanel: isWorkOrderPage,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'name',
    headerName: 'Element Name',
    minWidth: 160,
    flex: 1,
    filter: 'agTextColumnFilter',
    tooltipField: 'name',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    initialHide: isWorkOrderPage && !isCutListView,
  },
  {
    field: 'benderProfile.name',
    headerName: 'Bender Profile',
    valueFormatter: ({ value, data }) => value ?? (data?.hasBendData ? '--' : ''),
    sortable: false,
    width: 200,
    initialHide: isCutListView,
  },
  {
    field: 'cutProfile.name',
    headerName: 'Cut Profile',
    valueFormatter: ({ value, data }) => value ?? (data?.length ? '--' : ''),
    sortable: false,
    width: 200,
    initialHide: isCutListView,
  },
  {
    field: 'comments',
    tooltipField: 'comments',
    width: 150,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    initialHide: isWorkOrderPage,
  },
  {
    colId: 'machineDataModified',
    headerName: 'Machine Data Modified',
    width: 200,
    sortable: false,
    initialHide: true,
    valueGetter: ({ data }) => getLatestMachineDataEdit(data),
    valueFormatter: ({ value }: { value: MachineDataEdit | null }) => formatDate(value?.modifiedOn, 'MM/dd/yyyy', ''),
    cellRenderer: CellRenderer<MachineElement, never, MachineDataEdit | null>(
      ({ data, value: latestEdit, valueFormatted }) => (
        <Flex gap="xs">
          <InfoIcon
            color={
              // If an External source overwrote any web changes, alert the user
              isNotNil(data.machineDataExternalUpdatedOn) &&
              isNotNil(data.machineDataWebUpdatedOn) &&
              latestEdit?.source === 'External'
                ? 'orange'
                : undefined
            }
            label={getMachineDataEditTooltip(data)}
          />
          {valueFormatted}
        </Flex>
      ),
    ),
  },
  {
    ...(isCutListView ? lockedColDef('right') : {}),
    field: 'length',
    headerName: isCutListView ? 'Cut Length' : 'Length',
    width: 110,
    // Note: length is stored in feet.
    valueFormatter: ({ value }: { value: number | null }) => (value ? convertInchesToImperialString(value * 12) : '--'),
    initialHide: isWorkOrderPage && !isCutListView,
  },
  {
    ...(isCutListView ? lockedColDef('right') : {}),
    colId: 'cutIndex',
    field: 'cutIndex',
    width: 100,
    initialHide: !isCutListView,
    cellRenderer: CellRenderer<MachineElement, 'cutIndex'>(({ data, value }) =>
      data.cutOptimizationError ? <InfoIcon color="red" label={data.cutOptimizationError} /> : value,
    ),
  },
  {
    ...(isCutListView ? lockedColDef('right') : {}),
    field: 'bendMarkedDoneOn',
    headerName: 'Bent',
    width: 80,
    valueFormatter: ({ value }) => formatDate(value, 'PPPppp'),
    tooltipValueGetter: ({ value, valueFormatted }) => (value ? valueFormatted : undefined),
    cellRenderer: CellRenderer<MachineElement, 'bendMarkedDoneOn'>(({ value }) => (
      <EvolveIcon color="primary" icon={isNotNil(value) ? 'Check' : null} />
    )),
  },
  {
    ...(isCutListView ? lockedColDef('right') : {}),
    field: 'cutMarkedDoneOn',
    headerName: 'Cut',
    width: 80,
    valueFormatter: ({ value }) => formatDate(value, 'PPPppp'),
    tooltipValueGetter: ({ value, valueFormatted }) => (value ? valueFormatted : undefined),
    cellRenderer: CellRenderer<MachineElement, 'cutMarkedDoneOn'>(({ value }) => (
      <EvolveIcon color="primary" icon={isNotNil(value) ? 'Check' : null} />
    )),
  },
  {
    ...lockedColDef('right'),
    colId: 'validation',
    field: 'bendValidationStatus',
    headerName: 'Validation',
    width: 120,
    minWidth: 120,
    filter: CustomSelectComponent<MachineElement['bendValidationStatus'] | ''>({
      label: 'Validation Status',
      data: [
        'Pass',
        'Fail',
        {
          label: 'Not validated',
          value: 'NotApplicable',
        },
        {
          label: 'All',
          value: '',
        },
      ],
      filterKey: 'bendValidationStatuses',
      defaultValue: '',
    }),
    cellRenderer: CellRenderer<MachineElement>(({ data }) => <BendValidationCell element={data} />, {
      show: ({ data }) => data.hasBendData,
    }),
  },
  {
    ...lockedColDef('right'),
    colId: 'view-bend',
    width: 130,
    minWidth: 130,
    cellRenderer: CellRenderer<MachineElement>(({ data, api, node }) => (
      <ViewBendQAButton
        element={data}
        onBendUpdated={(ue) => {
          onElementUpdated?.(ue);
          // If the updated element has been marked as `Done`,
          // and we're possibly only viewing active elements,
          // we need to refresh the entire grid.
          if ((isNotNil(ue.bendMarkedDoneOn) || isNotNil(ue.cutMarkedDoneOn)) && !isWorkOrderPage) {
            refreshGrid(api);
          }
          node.updateData(ue);
        }}
      />
    )),
    hide: !allowQaModal,
  },
  {
    ...lockedColDef('right'),
    width: 60,
    cellRenderer: CellRenderer<MachineElement>(({ data, node, api }) => (
      <ElementMenu
        machineElement={data}
        onElementUpdated={(ue) => {
          onElementUpdated?.(ue);
          // Same as above
          if ((isNotNil(ue.bendMarkedDoneOn) || isNotNil(ue.cutMarkedDoneOn)) && !isWorkOrderPage) {
            refreshGrid(api);
          }
          node.updateData(ue);
        }}
        onEditElementData={onEditElementData}
        onEditBendData={onEditBendData}
        onRemoveElement={isWorkOrderPage ? onRemoveElement : undefined}
        onChangeBenderProfile={isWorkOrderPage ? onChangeBenderProfile : undefined}
      />
    )),
  },
  ...getCustomPropertyColDefs<MachineElement>(customElementProperties),
];
